// Minimal API client for the React app: mirrors the vanilla frontend's api():
// same-origin /api, cookie session (credentials: 'include'), JSON in/out, 401 →
// a global 'auth-required' event so the shell can re-gate.

export const API_BASE = '/api';

// Read-only gate: when the signed-in user's role is read-only (viewer, or agency
// Analyst), block writes client-side with a friendly error before they hit the
// server (the backend enforces the same, this just makes the UX immediate).
// Mirrors the server's allow-list: own security + logout still go through.
let _readOnly = false;
export function setReadOnly(v) { _readOnly = !!v; }
export function isReadOnly() { return _readOnly; }
const _roAllowed = (p) => p === '/auth/logout' || p === '/account/password' || p.startsWith('/account/2fa') || p.startsWith('/account/sessions') || p.startsWith('/panel-workspaces');

export async function api(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  if (_readOnly && method !== 'GET' && method !== 'HEAD' && !_roAllowed(path)) {
    const err = new Error('Your access is read-only.'); err.status = 403; err.code = 'READ_ONLY';
    throw err;
  }
  // silent401 is ours, not fetch's: keep it out of the request init.
  const { silent401, ...fetchOptions } = options;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...fetchOptions,
  });

  let data = null;
  try { data = await res.json(); } catch { /* non-JSON / empty */ }

  if (!res.ok) {
    // Only OUR auth layer's 401 (code AUTH_REQUIRED) means the session died →
    // re-gate the whole app. Upstream 401s that routes pass through (a
    // rejected Anthropic key, WordPress credentials, a proxy) are ordinary
    // errors the screen shows - bouncing the user into the SSO redirect for
    // those looked like being "signed out randomly". Background fetches for
    // non-essential chrome pass `silent401` so even a real session death in a
    // widget request can't bounce the user mid-action.
    if (res.status === 401 && data && data.code === 'AUTH_REQUIRED' && !silent401) document.dispatchEvent(new CustomEvent('auth-required'));
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  // A successful write means some module's contents changed: announce it so
  // live counters (the sidebar badges) refresh without a page reload. Emitted
  // here rather than at each call site so every module gets it for free, and
  // no future mutation can forget to fire it.
  if (method !== 'GET' && method !== 'HEAD') {
    document.dispatchEvent(new CustomEvent('data-changed', { detail: { path, method } }));
  }
  return data;
}

// Paged list reader for the WP proxy routes (/wp/posts, /wp/pages). WordPress
// caps a request at 100 rows, so a site with more content needs several, this
// walks every page and reports the site's real total, plus `truncated` when the
// safety ceiling stopped us short (never silently drop rows without saying so).
// `path` must already carry its query string (siteUrl, status, …).
const LIST_PER_PAGE = 100;
const LIST_MAX_PAGES = 10;   // 1000 rows: a ceiling, not an expected case

export async function apiList(path, options = {}) {
  const perPage  = options.perPage || LIST_PER_PAGE;
  const maxPages = options.maxPages || LIST_MAX_PAGES;
  const sep = path.includes('?') ? '&' : '?';
  const items = [];
  let total = 0, totalPages = 1, page = 1;

  while (page <= Math.min(maxPages, totalPages)) {
    const res = await fetch(`${API_BASE}${path}${sep}perPage=${perPage}&page=${page}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    let data = null;
    try { data = await res.json(); } catch { /* non-JSON / empty */ }
    if (!res.ok) {
      if (res.status === 401 && data && data.code === 'AUTH_REQUIRED') document.dispatchEvent(new CustomEvent('auth-required'));
      const err = new Error((data && data.error) || `Request failed (${res.status})`);
      err.status = res.status; err.data = data;
      throw err;
    }
    if (page === 1) {
      total      = parseInt(res.headers.get('X-WP-Total') || '0', 10) || 0;
      totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10) || 1;
    }
    const batch = Array.isArray(data) ? data : [];
    items.push(...batch);
    if (batch.length < perPage) break;   // last (or only) page
    page += 1;
  }

  return { items, total: total || items.length, truncated: totalPages > maxPages };
}

// Auth session probe. Returns { authenticated, auth_required, user?, account?, role?, impersonation? }.
// Never throws: a down/unconfigured backend resolves to a safe "not authenticated, not required".
export async function getMe() {
  try {
    return await api('/auth/me');
  } catch {
    return { authenticated: false, auth_required: false };
  }
}

export const logout   = () => api('/auth/logout', { method: 'POST', body: '{}' });

// Workspace invites (the ?invite=<token> email link). Lookup is public;
// accept is passwordless (SSO cutover): it attaches the membership, then the
// invitee signs in with their ImyBoss ID.
export const getInvite    = (token) => api(`/auth/invite?token=${encodeURIComponent(token)}`);
export const acceptInvite = (token, name) => api('/auth/accept-invite', { method: 'POST', body: JSON.stringify({ token, name: name || undefined }) });

// ImyBoss SUITE workspaces (SUITE-BOARD SD-001/002/003): the suite-level
// container shared across ImyBoss apps, NOT the MarketLens account.
// Shape: [{ id, name, initial, slug, current }]
export const listPanelWorkspaces = () => api('/panel-workspaces').then((d) => (d && d.workspaces) || []);
export const switchPanelWorkspace = (id) => api(`/panel-workspaces/${id}/switch`, { method: 'POST', body: '{}' });
